import { actionProps } from "@/lib/controlAnalytics";
import { chipClass } from "@/components/common/controls";
import { BellIcon } from "@/components/icons";
import { formatCount } from "@/lib/format";
import { JOBS_SORT_LABEL, type JobsSort } from "@/lib/jobs";

/**
 * The count and its line, a visitor's way to new matches by email, and the
 * order. A signed-in person's email about new matches is a setting, not the
 * board's. On a phone the chip is left out: the board there is the search
 * and the list, and the sign-up for it is still where a link asks for it.
 */
export default function JobsToolbar({
    total,
    line,
    visitor,
    onAlerts,
    sorts,
    sort,
    onSort,
}: {
    total: number | undefined;
    /** The words after the count. Null leaves the count to the page's header. */
    line: string | null;
    visitor: boolean;
    onAlerts: () => void;
    sorts: JobsSort[];
    sort: JobsSort;
    onSort: (sort: JobsSort) => void;
}) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-3">
            {/* One row on a phone, cut with an ellipsis: a count that wrapped
                as the results landed pushed the list down under the thumb.
                The words are whole in the search box above. */}
            {line !== null && (
                <div className="flex min-w-0 max-w-full flex-col gap-1">
                    <p
                        data-testid="jobs-count-line"
                        className="m-0 text-[13.5px] text-fg-muted max-[760px]:truncate"
                    >
                        {total !== undefined && (
                            <span className="font-mono font-semibold text-fg">
                                {formatCount(total)}
                            </span>
                        )}{" "}
                        {line}
                    </p>
                </div>
            )}
            {/* On a phone this group is a row of its own from the first paint.
                Beside a count line not yet filled in, it fit on the count's
                row, and the count's words, once they came, pushed it down a
                row and the list with it: a layout shift of 0.1 for a signed
                in person, whose row had no email chip to fill it
                (e2e/lighthouse). */}
            <div className="flex flex-wrap items-center gap-2 max-[760px]:basis-full">
                {visitor && (
                    <button
                        {...actionProps("join_dialog_opened", {
                            source: "Jobs",
                            section: "alerts",
                        })}
                        type="button"
                        onClick={onAlerts}
                        className={`${chipClass(false)} gap-1.5 max-[760px]:hidden`}
                    >
                        <BellIcon className="size-3.5" />
                        Get new matches by email
                    </button>
                )}
                {/* On a phone the order has a line of its own from the first paint.
                    Beside the email chip it sat at the edge, and the web font,
                    wider than some fallbacks, wrapped it down once it loaded: a
                    layout shift of 0.1 (e2e/lighthouse). */}
                <div className="max-[760px]:basis-full">
                    <div
                        role="group"
                        aria-label="Sort"
                        className="inline-flex rounded-[11px] border border-line bg-card p-[3px] max-[760px]:flex max-[760px]:w-full"
                    >
                        {sorts.map((option) => (
                            <button
                                {...actionProps("job_sort_selected", { source: "Jobs", option })}
                                key={option}
                                type="button"
                                aria-pressed={sort === option}
                                onClick={() => onSort(option)}
                                className={`h-[30px] rounded-lg px-3 text-[12px] whitespace-nowrap max-[760px]:min-h-11 max-[760px]:flex-auto ${
                                    sort === option
                                        ? "bg-fg font-semibold text-on-fg"
                                        : "font-medium text-fg-muted hover:text-fg"
                                }`}
                            >
                                {JOBS_SORT_LABEL[option]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
